import type React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

/** Sets the browser tab title, so screen-reader users hear where they've landed. */
export function usePageTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} · Aspect Support Desk`;
  }, [title]);
}

/** The value, once it has stopped changing for `delayMs` (e.g. search-as-you-type). */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Close a popover on a click outside it or on Escape (returning focus to its button). */
export function useDismiss(
  open: boolean,
  close: () => void,
  wrapper: React.RefObject<HTMLElement | null>,
  button: React.RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!wrapper.current?.contains(event.target as Node)) close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        close();
        button.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close, wrapper, button]);
}

/**
 * A header that stays at the top of the page: its height goes on its parent as --page-head, so
 * what sticks below it (the split-view pane) can sit under it and keep to the window.
 */
export function useStickyHead<T extends HTMLElement>() {
  const head = useRef<T>(null);
  useLayoutEffect(() => {
    const el = head.current;
    const page = el?.parentElement;
    if (!el || !page) return;
    const update = () => {
      page.style.setProperty("--page-head", `${el.offsetHeight}px`);
      window.dispatchEvent(new Event("resize")); // let anything placed below it re-place itself
    };
    update();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(update) : null;
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      page.style.removeProperty("--page-head");
    };
  }, []);
  return head;
}
